<script setup>
import { computed, ref, nextTick, watch } from 'vue'

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    x: {
        type: Number,
        default: 0
    },
    y: {
        type: Number,
        default: 0
    },
    content: {
        type: [String, Array],
        default: () => []
    },
    onItemClick: {
        type: Function,
        default: null
    }
})

const tooltipRef = ref(null)
const adjustedPosition = ref({ x: 0, y: 0 })

const handleItemClick = (item, index) => {
    if (props.onItemClick) {
        props.onItemClick(item, index)
    }
}

// 计算自适应位置
const calculatePosition = async (retryCount = 0) => {
    console.log('calculatePosition called, retry:', retryCount)
    if (!props.visible || !tooltipRef.value) {
        console.log('Early return:', { visible: props.visible, tooltipRef: !!tooltipRef.value })
        return
    }
    
    await nextTick()
    console.log('After nextTick')
    
    const tooltip = tooltipRef.value
    if (!tooltip) {
        console.log('No tooltip element found')
        return
    }
    
    // 等待tooltip渲染完成
    let rect = tooltip.getBoundingClientRect()
    console.log('Tooltip rect:', rect)
    
    // 如果尺寸为0，说明还没渲染完成，最多重试3次
    if ((rect.width === 0 || rect.height === 0) && retryCount < 3) {
        console.log('Tooltip not rendered yet, waiting...')
        setTimeout(() => {
            calculatePosition(retryCount + 1)
        }, 50)
        return
    }
    
    // 如果重试3次后还是没有尺寸，使用默认值
    if (rect.width === 0 || rect.height === 0) {
        console.log('Using default size')
        rect = { width: 200, height: 100, ...rect }
    }
    
    // 获取页面容器的边界
    const pageContainer = document.querySelector('.page-container') || document.querySelector('.app-container') || document.body
    const containerRect = pageContainer.getBoundingClientRect()
    console.log('Container rect:', containerRect)
    
    // 将绝对坐标转换为相对于容器的坐标
    const relativeX = props.x - containerRect.left
    const relativeY = props.y - containerRect.top
    
    let newX = relativeX
    let newY = relativeY
    
    console.log('Original position:', { x: relativeX, y: relativeY })
    console.log('Tooltip size:', { width: rect.width, height: rect.height })
    console.log('Container size:', { width: containerRect.width, height: containerRect.height })
    
    // 检查右边界 - 相对于容器宽度
    if (newX + rect.width > containerRect.width - 20) {
        newX = Math.max(10, newX - rect.width - 10)
        console.log('Adjusted for right boundary:', newX)
    }
    
    // 检查左边界
    if (newX < 10) {
        newX = 10
        console.log('Adjusted for left boundary:', newX)
    }
    
    // 检查下边界 - 相对于容器高度
    if (newY + rect.height > containerRect.height - 20) {
        newY = Math.max(10, newY - rect.height - 20)
        console.log('Adjusted for bottom boundary:', newY)
    }
    
    // 检查上边界
    if (newY < 10) {
        newY = 10
        console.log('Adjusted for top boundary:', newY)
    }
    
    // 最终边界检查，确保不会超出容器
    newX = Math.min(Math.max(10, newX), containerRect.width - rect.width - 10)
    newY = Math.min(Math.max(10, newY), containerRect.height - rect.height - 10)
    
    // 转换回绝对坐标
    const finalPosition = { 
        x: newX + containerRect.left, 
        y: newY + containerRect.top 
    }
    console.log('Final position:', finalPosition)
    adjustedPosition.value = finalPosition
}

// 监听位置和可见性变化
watch([() => props.visible, () => props.x, () => props.y], ([visible, x, y]) => {
    console.log('Watch triggered:', { visible, x, y })
    if (visible) {
        // 初始位置设为原始位置
        adjustedPosition.value = { x: props.x, y: props.y }
        // 然后计算调整后的位置
        console.log('Calling calculatePosition')
        calculatePosition()
    }
}, { immediate: true })

// 也可以在visible变为true时立即计算
watch(() => props.visible, (newVisible) => {
    if (newVisible) {
        console.log('Visible changed to true, recalculating position')
        setTimeout(() => {
            calculatePosition()
        }, 0)
    }
})

const tooltipStyle = computed(() => ({
    left: `${adjustedPosition.value.x}px`,
    top: `${adjustedPosition.value.y}px`,
    display: props.visible ? 'flex' : 'none'
}))

const isLanguageTooltip = computed(() => {
    return Array.isArray(props.content) && props.content.length > 0
})
</script>
<template>
    <div class="sc-hjsuWn hiMnyo" :style="tooltipStyle" v-if="props.visible" ref="tooltipRef">
        <div class="tooltipBorderInside_v tooltipAnimate_toB" style="pointer-events: auto; top: 19.25px;">
            <div class="tooltipBorderInsideB_bottom" style="transform: scaleY(-1);">
                <div>
                    <div class="justLine" style="width: 20px; background-color: var(--box-nested-black-xl-border);">
                    </div>
                    <div class="tooltipBorderInside_faceLeft"
                        style="clip-path: path(&quot;M 0 0 C 10 0 12.5 7.5 20 7.5 L 20 0 Z&quot;); width: 20px; height: 10px;">
                    </div>
                    <div class="tooltipBorderInside_faceLeft"
                        style="clip-path: path(&quot;M 0 0 C 10 0 12.5 7.5 20 7.5 L 20 0 Z&quot;); width: 20px; height: 10px; transform: scaleX(-1);">
                    </div>
                </div>
                <div style="position: absolute; top: -1.5px;">
                    <div class="justLine" style="width: 20px; background-color: var(--box-nested-white-xl-max);"></div>
                    <div class="tooltipBorderInside_faceLeft tooltipBorderInside_faceLeft_top"
                        style="clip-path: path(&quot;M 0 0 C 10 0 12.5 7.5 20 7.5 L 20 0 Z&quot;); width: 20px; height: 10px; background-color: var(--box-nested-white-xl-max);">
                    </div>
                    <div class="tooltipBorderInside_faceLeft tooltipBorderInside_faceLeft_top"
                        style="clip-path: path(&quot;M 0 0 C 10 0 12.5 7.5 20 7.5 L 20 0 Z&quot;); width: 20px; height: 10px; transform: scaleX(-1); background-color: var(--box-nested-white-xl-max);">
                    </div>
                </div>
            </div>
            <div class="tooltipBorderInsideB_top" style="border-radius: 10px; margin-left: 0px; margin-right: 0px;">
                <div class="content" style="flex-direction: column; white-space: normal;">
                    <!-- 语言选择内容 -->
                    <template v-if="isLanguageTooltip">
                        <div class="sc-bRKDuR hyNOSv" v-for="(language, index) in content" :key="language">
                            <div class="btnInside white" @click="handleItemClick(language, index)">
                                <div style="flex: 1 1 0%; text-align: center;">
                                    <p style="white-space: nowrap; line-height: 1em;">{{ language }}</p>
                                </div>
                            </div>
                        </div>
                    </template>
                    <!-- 普通文本内容 -->
                    <template v-else>
                        <div class="tooltip-text">{{ content }}</div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.hiMnyo {
    position: absolute;
    z-index: 5000;
    display: flex;
    justify-content: center;
    max-height: none;
    pointer-events: none;

    .tooltipBorderInside_v {
        visibility: hidden;
        min-height: 2rem;
        min-width: 4.75rem;
        position: absolute;
    }

    .tooltipAnimate_toB {
        visibility: visible;
        animation: 0.1s ease 0s 1 normal none running scaleIn_bottom;
        pointer-events: auto;
    }

    .tooltipBorderInsideB_bottom,
    .tooltipBorderInsideB_bottom>div {
        width: 100%;
        display: flex;
        justify-content: center;
        position: relative;
    }

    .tooltipBorderInsideB_top {
        min-width: 4.75rem;
        min-height: 2rem;
        border: 0.09375rem solid var(--box-nested-black-xl-border);
        background-color: var(--box-nested-white-xl-max);
        box-shadow: rgba(0, 0, 0, 0.3) 0px 0.0625rem 0.3125rem;
        overflow: hidden;
    }

    .justLine {
        height: 0.0625rem;
        position: absolute;
    }

    .tooltipBorderInside_faceLeft {
        background-color: var(--box-nested-black-xl-border);
        position: relative;
    }

    .tooltipBorderInside_faceLeft::before {
        content: "";
        position: absolute;
        top: -2px;
        width: 110%;
        height: 2px;
        background-color: var(--box-nested-black-xl-border);
    }

    .tooltipBorderInside_faceLeft::after {
        content: "";
        position: absolute;
        right: -1px;
        width: 2px;
        height: 100%;
        background-color: var(--box-nested-black-xl-border);
    }

    .content {
        padding: 0.5rem;
        font-weight: 700;
        display: flex;
        justify-content: center;
        gap: 0.5rem;
    }
}

.hyNOSv {
    display: inline-block;

    .btnInside.white {
        background-color: var(--button-white-xl);
        color: var(--text-black-l-title);
    }

    .btnInside {
        width: 100%;
        overflow: hidden;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 0.75rem;
        font-weight: 700;
        cursor: pointer;
        padding: 0px 0.75rem;
        border-radius: 0.5rem;
        height: 1.5625rem;
        max-height: 100%;
    }
}

@keyframes scaleIn_bottom {
    0% {
        transform: scale(0.8);
        transform-origin: center top;
    }

    100% {
        transform: scale(1);
        transform-origin: center top;
    }
}

.tooltip-text {
    padding: 0.5rem;
    color: var(--text-black-l-title);
    font-size: 0.75rem;
    font-weight: 700;
    text-align: center;
}
</style>